import type {
  ComboboxButtonProps as HeadlessComboboxButtonProps,
  ComboboxInputProps as HeadlessComboboxInputProps,
  ComboboxOptionProps as HeadlessComboboxOptionProps,
  ComboboxOptionsProps as HeadlessComboboxOptionsProps,
  ComboboxProps as HeadlessComboboxProps,
} from '@headlessui/react';
import {
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Combobox as HeadlessCombobox,
} from '@headlessui/react';
import classNames from 'classnames';
import type { ElementType, ExoticComponent, ReactNode } from 'react';
import React, { Fragment } from 'react';
import styles from './HeadlessCombobox.module.scss';

type ComboboxProps<
  TValue = any,
  TMultiple extends boolean | undefined = true,
  TTag extends ElementType = 'div',
> = { className?: string } & HeadlessComboboxProps<TValue, TMultiple, TTag>;

const Container = (props: ComboboxProps) => {
  const { children, className, ...rest } = props;
  return (
    <HeadlessCombobox as="div" className={classNames(styles.container, className)} {...rest}>
      {children}
    </HeadlessCombobox>
  );
};

type ComboboxButtonProps<TTag extends ElementType = ExoticComponent> = {
  children: ReactNode;
  className?: string;
} & HeadlessComboboxButtonProps<TTag>;

const Button = (props: ComboboxButtonProps) => {
  const { className, children, ...rest } = props;
  return (
    <ComboboxButton as={Fragment} {...rest}>
      {({ value }) => (
        <div
          className={classNames(styles.button, className, {
            [styles.hasSelected]: value !== null && (!Array.isArray(value) || value.length > 0),
          })}
        >
          {children}🔽
        </div>
      )}
    </ComboboxButton>
  );
};

type ComboboxInputProps<TTag extends ElementType = 'input', TType = any> = {
  className?: string;
} & HeadlessComboboxInputProps<TTag, TType>;

const Input = React.memo((props: ComboboxInputProps) => {
  const { children, className, ...rest } = props;
  return (
    <ComboboxInput as="input" className={classNames(styles.searchInput, className)} {...rest}>
      {children}
    </ComboboxInput>
  );
});

type ComboboxOptionsProps<TTag extends ElementType = 'ul'> = {
  className?: string;
} & HeadlessComboboxOptionsProps<TTag>;

const Options = (props: ComboboxOptionsProps) => {
  const { children, className, ...rest } = props;
  return (
    <ComboboxOptions as="ul" className={classNames(styles.optionList, className)} {...rest}>
      {children}
    </ComboboxOptions>
  );
};

type ComboboxOptionProps<TTag extends ElementType = 'li', TType = string> = {
  children: React.ReactNode;
  showCheckmark?: boolean;
  innerClassName?: string;
  className?: string;
} & Omit<HeadlessComboboxOptionProps<TTag, TType>, 'children'>;

const Option = (props: ComboboxOptionProps) => {
  const { className, innerClassName, showCheckmark = true, children, ...rest } = props;
  return (
    <ComboboxOption as="li" className={classNames(styles.option, className)} {...rest}>
      {({ selected }) => (
        <>
          {showCheckmark && <div className={styles.checkmark}>{selected && '✅'}</div>}
          <div className={classNames(styles.inner, innerClassName)}>{children}</div>
        </>
      )}
    </ComboboxOption>
  );
};

export const Combobox = Object.assign(Container, { Button, Input, Options, Option });
